import { useEditor, useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { MediaItem } from "@/content-hub/page/domain";
import {
  getOrganizationExternalId,
  getPageSlug,
  getPageUrl,
  getRecordingById,
} from "@/content-hub/store/selectors";
import { buildContentHubPublishedVideoDeepLink } from "@/content-hub/utils/build-content-hub-share-page-url";
import { getCraftNodeDom } from "@/content-hub/utils/get-craft-node-dom";
import { resolveVideoBlockPosterForDetails } from "@/content-hub/utils/resolve-video-block-poster-for-details";
import type { RootState } from "@/redux/store";
import { useAppSelector } from "@/redux/store";

import {
  effectiveRecordingDisplay,
  hasCustomRecordingDisplayOverrides,
} from "../recording-metadata-merge";
import {
  displayThumbnailUrlFromUpload,
  RecordingVideoDetailsFields,
} from "../recording-video-details-fields";
import type { VideoBlockProps } from "../video";

export const VideoShareUrlSetting = () => {
  const flags = useFlags();
  const directVideoSharingEnabled = Boolean(flags.contentHubDirectVideoSharing);

  const {
    props,
    id,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.props as VideoBlockProps,
    id: node.id,
  }));

  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const pageSlug = useAppSelector(getPageSlug);
  const pageUrl = useAppSelector(getPageUrl);
  const { query } = useEditor();

  const recording = useSelector((state: RootState) =>
    props.recordingId ? getRecordingById(props.recordingId)(state) : undefined
  );

  const mergedRecordingDisplay =
    recording &&
    effectiveRecordingDisplay(recording, {
      displayTitle: props.displayTitle,
      displayDescription: props.displayDescription,
      displayThumbnail: props.displayThumbnail,
    });

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";

    let videoId: string | null = null;
    let videoName: string | null = null;

    const resolvedVideoName = mergedRecordingDisplay
      ? mergedRecordingDisplay.title || null
      : props.displayTitle ?? props.title ?? null;

    if (props.recordingId) {
      videoId = props.recordingId;
      videoName = resolvedVideoName;
    } else if (props.url) {
      videoId = id;
      videoName = resolvedVideoName;
    }

    if (!videoId) return "";

    return buildContentHubPublishedVideoDeepLink({
      organizationExternalId,
      pageSlug,
      pageUrl: pageUrl || null,
      getSectionRootElement: () => getCraftNodeDom(query, id),
      videoId,
      videoName,
    });
  }, [
    id,
    organizationExternalId,
    pageSlug,
    pageUrl,
    props.recordingId,
    props.title,
    props.url,
    props.displayTitle,
    mergedRecordingDisplay,
    query,
  ]);

  const hasVideo = Boolean(props.recordingId || props.url);
  if (!hasVideo) return null;

  return (
    <div className="border-t border-neutral-b4 px-4 pb-4 pt-3">
      <RecordingVideoDetailsFields
        analytics={
          props.recordingId
            ? { kind: "recording", recordingId: props.recordingId }
            : { kind: "embed", id: id }
        }
        localReadonlyShareUrl={directVideoSharingEnabled ? shareUrl : undefined}
        displayTitle={props.displayTitle ?? ""}
        displayDescription={props.displayDescription ?? ""}
        displayThumbnail={props.displayThumbnail || undefined}
        thumbnail={resolveVideoBlockPosterForDetails({
          blockThumbnail: props.thumbnail,
          embedUrl: props.url,
          recordingFallbackThumbnail: recording?.thumbnail,
        })}
        uploadTransactionId={id}
        titlePlaceholder={recording?.title ?? props.title}
        descriptionPlaceholder={recording?.description ?? undefined}
        onTitleChange={(value) =>
          setProp((p: VideoBlockProps) => {
            p.displayTitle = value || undefined;
          })
        }
        onDescriptionChange={(value) =>
          setProp((p: VideoBlockProps) => {
            p.displayDescription = value || undefined;
          })
        }
        onThumbnailUpload={(imageMetadata: MediaItem) =>
          setProp((p: VideoBlockProps) => {
            p.displayThumbnail = displayThumbnailUrlFromUpload(imageMetadata);
          })
        }
        showReset={hasCustomRecordingDisplayOverrides({
          displayTitle: props.displayTitle,
          displayDescription: props.displayDescription,
          displayThumbnail: props.displayThumbnail,
        })}
        onReset={() =>
          setProp((p: VideoBlockProps) => {
            p.displayTitle = undefined;
            p.displayDescription = undefined;
            p.displayThumbnail = undefined;
          })
        }
      />
    </div>
  );
};
